import { useEffect, useRef, useState } from 'react';
import { defineMaterial, type MaterialComponentProps } from '@h5/schema';
import { GalleryPropsSchema, type GalleryProps } from './schema';

export { GalleryPropsSchema, type GalleryProps };

/**
 * 图集物料：CSS scroll-snap 横滑，不依赖第三方库。
 * 自动轮播用 scrollTo 驱动，滚动事件回算当前索引；用户触摸期间暂停。
 */
function GalleryView({ props, ctx }: MaterialComponentProps<GalleryProps>) {
  const ref = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const touching = useRef(false);
  const count = props.images.length;
  const width = Math.ceil(ctx.layer.rect.w * ctx.scale * 2);

  // 自动轮播（编辑态关闭，避免画布里乱动）
  useEffect(() => {
    if (ctx.mode !== 'view' || !props.autoplay || count < 2) return;
    const timer = setInterval(() => {
      if (touching.current || !ref.current) return;
      const next = (index + 1) % count;
      ref.current.scrollTo({ left: next * ref.current.clientWidth, behavior: 'smooth' });
    }, props.interval);
    return () => clearInterval(timer);
  }, [ctx.mode, props.autoplay, props.interval, count, index]);

  const onScroll = () => {
    const el = ref.current;
    if (!el || el.clientWidth === 0) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index) setIndex(i);
  };

  if (count === 0) {
    return (
      <div style={{ width: '100%', height: '100%', background: '#f0f0f0', border: '1px dashed #ccc', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12 }}>
        请添加图片
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      <div
        ref={ref}
        className="h5-gallery-track"
        onScroll={onScroll}
        onTouchStart={() => { touching.current = true; }}
        onTouchEnd={() => { touching.current = false; }}
        style={{
          width: '100%', height: '100%', display: 'flex',
          overflowX: 'auto', overflowY: 'hidden',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <style>{'.h5-gallery-track::-webkit-scrollbar{display:none}'}</style>
        {props.images.map((src, i) => (
          <img
            key={`${src}-${i}`}
            src={ctx.assets.url(src, { width })}
            alt=""
            draggable={false}
            style={{ flex: '0 0 100%', width: '100%', height: '100%', objectFit: props.fit, scrollSnapAlign: 'start', display: 'block', pointerEvents: 'none' }}
          />
        ))}
      </div>
      {props.showDots && count > 1 ? (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 8, display: 'flex', justifyContent: 'center', gap: 6, pointerEvents: 'none' }}>
          {props.images.map((_, i) => (
            <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: i === index ? '#fff' : 'rgba(255,255,255,.5)' }} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export const galleryMaterial = defineMaterial<GalleryProps>({
  id: 'gallery',
  version: 1,
  name: '图集',
  category: 'interactive',
  icon: '⧉',
  defaultSize: { w: 335, h: 200 },
  defaultProps: GalleryPropsSchema.parse({}),
  propsSchema: GalleryPropsSchema,
  component: GalleryView,
});
